import React, { useEffect, useState } from "react";
import { PageHeader, Loader, Badge, Empty } from "@/components/shared";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { fmtPKR, fmtDate, statusColor } from "@/lib/format";

export default function MyPredictions() {
  const { t } = useI18n();
  const [preds, setPreds] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/predictions").then(({ data }) => setPreds(data)).catch(() => {}); }, []);
  if (!preds) return <Loader />;

  const filters = ["all", "pending", "active", "locked", "won", "lost", "draw"];
  const shown = filter === "all" ? preds : preds.filter((p) => p.status === filter);

  return (
    <div>
      <PageHeader title={t("my_predictions")} subtitle="Every confirmed prediction appears here automatically." />
      <div className="flex flex-wrap gap-2 mb-6">
        {filters.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold border capitalize ${filter === f ? "border-orange-500 bg-orange-500/15 text-orange-400" : "border-white/10 text-gray-400 hover:text-white"}`}>{f}</button>
        ))}
      </div>
      {shown.length === 0 ? <Empty label="No predictions found." /> : (
        <div className="space-y-4">
          {shown.map((p) => (
            <div key={p.id} className="ec-card p-5" data-testid={`prediction-${p.prediction_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-head font-semibold text-white">{p.event_name}</h3>
                    <Badge className={statusColor(p.status)}>{p.status}</Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 keep-ltr">{p.prediction_id} · {fmtDate(p.created_at)}</p>
                </div>
                <div className="text-end">
                  <p className="text-xs text-gray-500">{t("reward_amount")}</p>
                  <p className={`font-head font-bold ${p.status === "won" ? "text-emerald-400" : "text-white"}`}>{fmtPKR((p.status === "lost" || p.status === "draw") ? 0 : p.reward_amount)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 text-sm">
                <div><p className="text-xs text-gray-500">{t("teams")}</p><p className="text-gray-200">{p.team_a} vs {p.team_b}</p></div>
                <div><p className="text-xs text-gray-500">{t("selected_team")}</p><p className="text-orange-400 font-semibold">{p.selected_team}</p></div>
                <div><p className="text-xs text-gray-500">{t("amount")}</p><p className="text-gray-200">{fmtPKR(p.amount)}</p></div>
                <div><p className="text-xs text-gray-500">{t("reward_percentage")}</p><p className="text-gray-200">{p.reward_percentage}%</p></div>
              </div>
              {p.final_result && <p className="text-xs mt-3 text-gray-400">{t("final_result")}: <span className={p.status === "won" ? "text-emerald-400" : p.status === "draw" ? "text-amber-400" : "text-red-400"}>{p.final_result}</span></p>}
              {(p.status === "won" || p.status === "lost" || p.status === "draw") && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-3 pt-3 border-t border-white/5 text-sm">
                  <div><p className="text-xs text-gray-500">Returned Amount</p><p className="text-gray-200">{fmtPKR(p.returned_amount ?? 0)}</p></div>
                  <div><p className="text-xs text-gray-500">Reward Amount</p><p className="text-emerald-400 font-semibold">{fmtPKR(p.status === "won" ? p.reward_amount : 0)}</p></div>
                  <div><p className="text-xs text-gray-500">Final Balance</p><p className="text-gray-200">{p.final_balance != null ? fmtPKR(p.final_balance) : "-"}</p></div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
